import { useEffect } from 'react';
import { useParams, useLocation, Outlet } from 'react-router-dom';
import { useCampaignStore } from '../stores/campaignStore';
import { AlertCircle, Gift } from 'lucide-react';
import { useAnalytics } from '../hooks/useAnalytics';
import riachueloLogo from '../assets/riachuelo-logo.svg';
import { CampaignHeader } from '../components/CampaignHeader';
import { useNavigate } from 'react-router-dom';

/**
 * Wrapper route that loads a campaign by slug.
 * Renders children (Outlet) once the campaign is ready.
 */
export function CampaignLoader() {
  const { campaignSlug } = useParams<{ campaignSlug: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { campaign, status, error, loadCampaignBySlug } = useCampaignStore();
  const { trackException, trackSelectContent } = useAnalytics();

  // Voucher lookup/redeem routes must stay reachable even when the campaign has ended or
  // reservations are closed — that's exactly when customers who already reserved need them.
  // '/confirmacao' is included because ConsultVoucher navigates there to display the voucher
  // of an existing reservation (Step4Confirmation doubles as the voucher display screen).
  const isVoucherLookupRoute = location.pathname.includes('/consultar-voucher')
    || location.pathname.includes('/voucher-resgatado')
    || location.pathname.includes('/confirmacao');

  useEffect(() => {
    if (campaignSlug) {
      loadCampaignBySlug(campaignSlug);
    }
  }, [campaignSlug, loadCampaignBySlug]);

  useEffect(() => {
    if (status === 'error') trackException('campaign_load_failed');
    if (status === 'not-found') trackException('campaign_not_found');
  }, [status, trackException]);

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-rchlo-green mb-4" />
        <p className="text-sm text-gray-500">Carregando campanha...</p>
      </div>
    );
  }

  if (status === 'not-found') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-6">
        <div className="card max-w-md w-full text-center py-10">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={32} className="text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Campanha não encontrada</h2>
          <p className="text-sm text-gray-500">{error}</p>
          <a href="/" className="text-sm text-rchlo-green hover:underline mt-4 inline-block">
            Voltar ao início
          </a>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-6">
        <div className="card max-w-md w-full text-center py-10">
          <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={32} className="text-orange-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Erro ao carregar</h2>
          <p className="text-sm text-gray-500 mb-4">{error}</p>
          <button
            onClick={() => {
              trackSelectContent('button', 'recarregar_campanha', {
                eventCategory: 'campanha',
                eventAction: 'tentar_novamente',
                eventLabel: 'botao_recarregar_campanha',
              });
              if (campaignSlug) loadCampaignBySlug(campaignSlug);
            }}
            className="btn-primary"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  // Campaign scheduled — show "coming soon" state
  // Also show if campaign is Active but reservation period hasn't started yet.
  // Compare calendar dates in the browser's LOCAL timezone. Using toISOString()
  // here would convert "now" to UTC and, after 21:00 BRT, roll "today" to the
  // next day — prematurely releasing a campaign that starts tomorrow.
  const localToday = (() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  })();
  const isBeforeReservationStart = campaign?.status === 'Active'
    && campaign.startDate
    && new Date(`${campaign.startDate}T00:00:00`) > new Date(`${localToday}T00:00:00`);

  // Active but the reservation window (endDate = reservationEndDate) has already closed.
  // The campaign stays Active while redemption is still allowed, but new reservations must
  // not be offered — otherwise the customer registers, picks a store, and finds no dates at
  // step 3. Compare calendar dates in LOCAL timezone (same reasoning as the start check).
  const isAfterReservationEnd = campaign?.status === 'Active'
    && campaign.endDate
    && new Date(`${campaign.endDate}T00:00:00`) < new Date(`${localToday}T00:00:00`);

  if (campaign?.status === 'Scheduled' || isBeforeReservationStart) {
    const startDateFormatted = campaign.startDate
      ? new Date(`${campaign.startDate}T12:00:00`).toLocaleDateString('pt-BR')
      : '';

    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <header className="relative z-30 flex items-center justify-center bg-rchlo-green px-5 py-3">
          <img src={riachueloLogo} alt="Riachuelo" className="h-6 w-auto" />
        </header>

        {campaign.initialImageUrl && (
          <img
            src={campaign.initialImageUrl}
            alt={campaign.name}
            className="w-full object-cover max-h-[55vh]"
          />
        )}

        <div className="flex flex-col items-center px-6 py-10 text-center">
          {campaign.productImage && (
            <img
              src={campaign.productImage}
              alt={campaign.productName}
              className="h-20 object-contain mb-6"
            />
          )}
          <h1 className="text-xl font-bold text-rchlo-green leading-snug">
            Aguarde! Esta campanha iniciará em<br />{startDateFormatted}.
          </h1>
          <p className="text-sm text-gray-500 mt-3 max-w-xs">
            Em breve você poderá se cadastrar e retirar seu brinde exclusivo nas lojas Riachuelo.
          </p>
        </div>
      </div>
    );
  }

  // Campaign finished — show ended state (but still allow voucher lookup/redeem)
  if (campaign?.status === 'Finished' && !isVoucherLookupRoute) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <header className="relative z-30 flex items-center justify-center bg-rchlo-green px-5 py-3">
          <img src={riachueloLogo} alt="Riachuelo" className="h-6 w-auto" />
        </header>

        {/* Campaign image with dark overlay */}
        {campaign.initialImageUrl && (
          <div className="relative">
            <img
              src={campaign.initialImageUrl}
              alt={campaign.name}
              className="w-full object-cover max-h-[55vh] brightness-[0.6]"
            />
          </div>
        )}

        {/* Ended message */}
        <div className="flex flex-col items-center px-6 py-10 text-center">
          {campaign.productImage && (
            <img
              src={campaign.productImage}
              alt={campaign.productName}
              className="h-20 object-contain mb-6 opacity-70"
            />
          )}
          <h1 className="text-xl font-bold text-rchlo-green leading-snug">
            Que pena! Infelizmente essa campanha<br />já está encerrada.
          </h1>
          <p className="text-sm text-gray-500 mt-3 max-w-xs">
            Fique de olho nas próximas campanhas da Riachuelo e não perca as novidades.
          </p>
        </div>
      </div>
    );
  }

  // Reservations closed — campaign still Active (redemption ongoing) but the reservation
  // window has ended. Do NOT offer the participation flow; show a clear "reservations closed"
  // state so customers with an existing voucher can still redeem via the voucher-lookup flow.
  if (isAfterReservationEnd && !isVoucherLookupRoute) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <CampaignHeader />

        {campaign.initialImageUrl && (
          <div className="relative">
            <img
              src={campaign.initialImageUrl}
              alt={campaign.name}
              className="w-full object-cover max-h-[55vh] brightness-[0.6]"
            />
          </div>
        )}

        <div className="flex flex-col items-center px-6 py-10 text-center">
          {campaign.productImage && (
            <img
              src={campaign.productImage}
              alt={campaign.productName}
              className="h-20 object-contain mb-6 opacity-70"
            />
          )}
          <h1 className="text-xl font-bold text-rchlo-green leading-snug">
            As reservas desta campanha<br />já foram encerradas.
          </h1>
          <p className="text-sm text-gray-500 mt-3 max-w-xs">
            O período de reservas terminou. Se você já reservou seu brinde, consulte seu voucher
            para efetuar o resgate na loja.
          </p>
          <button
            type="button"
            onClick={() => navigate(`/${campaignSlug}/consultar-voucher`)}
            className="btn-primary w-full max-w-xs mt-8 uppercase tracking-wide font-bold"
          >
            Consultar Voucher
          </button>
        </div>
      </div>
    );
  }

  return <Outlet />;
}
